import { useState, useEffect, useRef, useCallback } from "react";
import { useTerminology } from "@/contexts/TerminologyContext";
import { useTranslation } from "react-i18next";
import { Mail, User, Pencil, Check, X, Briefcase, Phone, Search, Loader2, Plus } from "lucide-react";
import LinkedInIcon from "@/components/shared/LinkedInIcon";
import { enrichPerson } from "@/lib/enrichment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { companies } from "@/data/dummy";
import CompanyLogo from "@/components/shared/CompanyLogo";
import { useContactMutations } from "@/lib/hooks/use-contact-mutations";
import { useSearchCompaniesLazyQuery, useCreateAccountMutation } from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import EditableLocationField from "@/components/shared/EditableLocationField";
import { isLovablePreview } from "@/lib/utils/environment";
import AddCompanyForm from "@/components/shared/AddCompanyForm";

/**
 * Minimal shape returned to ``onCreated`` after a contact is added.
 * Production callers get the real ``externalId`` from the createContact
 * mutation; preview callers get a synthetic one so downstream wiring
 * (e.g. linking the new contact back to a Today-page daily lead) behaves
 * uniformly across modes.
 */
export interface AddPersonDialogCreated {
  externalId: string;
  primaryEmail: string;
  firstName: string;
  lastName: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd?: (person: { firstName: string; lastName: string; email: string; company: string; companyUrl: string; title: string; phone: string; location: string; linkedin: string }) => void;
  /**
   * Fires after a contact is successfully added — in production with the
   * real Contact returned by ``createContact``, in preview with a
   * synthetic record. Use this when the caller needs to chain a
   * follow-up action (e.g. ``saveLeadAsContact``) on the new contact's
   * ``externalId``.
   */
  onCreated?: (contact: AddPersonDialogCreated) => void | Promise<void>;
  accountId?: string;
  initialData?: Record<string, string>;
}

const domainCompanyMap: Record<string, { name: string; website: string }> = {};
companies.forEach(c => {
  const domain = c.website?.replace(/^(https?:\/\/)?(www\.)?/, "").split("/")[0];
  if (domain) domainCompanyMap[domain] = { name: c.name, website: c.website };
});

function extractDomain(email: string): string {
  const parts = email.split("@");
  return parts.length === 2 ? parts[1].toLowerCase() : "";
}

function faviconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
}

function guessCompanyName(domain: string): string {
  const known = domainCompanyMap[domain];
  if (known) return known.name;
  const name = domain.split(".")[0];
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function normalizeLinkedin(value: string): string {
  return value
    .replace(/^https?:\/\/(www\.)?linkedin\.com\/(in\/)?/, "")
    .replace(/\/$/, "")
    .trim();
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "");
  const national = digits.startsWith("1") && digits.length > 10 ? digits.slice(1) : digits;
  if (national.length <= 3) return national;
  if (national.length <= 6) return `(${national.slice(0, 3)}) ${national.slice(3)}`;
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6, 10)}`;
}


export default function AddPersonDialog({ open, onOpenChange, onAdd, onCreated, accountId, initialData }: Props) {
  const { terminology, singular } = useTerminology();
  const { t } = useTranslation();
  const { createContact, creating } = useContactMutations();
  const useBackend = !isLovablePreview();
  const workspaceId = useWorkspaceId();
  const [searchCompanies, { loading: companiesLoading }] = useSearchCompaniesLazyQuery();
  const [createAccountMutation] = useCreateAccountMutation();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyDomain, setCompanyDomain] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [domainMatched, setDomainMatched] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState("");
  const [enriching, setEnriching] = useState(false);
  const [showAddCompanyForm, setShowAddCompanyForm] = useState(false);
  const enrichedRef = useRef(false);

  // Pre-fill from initialData prop (fires whenever the dialog opens with new data)
  useEffect(() => {
    if (!open || !initialData) return;
    if (initialData.email) setEmail(initialData.email);
    if (initialData.firstName) setFirstName(initialData.firstName);
    if (initialData.lastName) setLastName(initialData.lastName);
    if (initialData.companyName) setCompanyName(initialData.companyName);
    if (initialData.companyDomain) {
      setCompanyDomain(initialData.companyDomain);
      setDomainMatched(true);
    }
    if (initialData.linkedin) setLinkedin(initialData.linkedin);
    if (initialData.jobTitle) setJobTitle(initialData.jobTitle);
    if (initialData.phone) setPhone(formatPhone(initialData.phone));
    const loc = [initialData.city, initialData.state, initialData.country].filter(Boolean).join(", ");
    if (loc) setLocation(loc);
  }, [open, initialData]);

  const [companySuggestions, setCompanySuggestions] = useState<Array<{ id: string; name: string; domain: string; logoUrl: string | null }>>([]);

  const fetchCompanySuggestions = useCallback(async (search: string) => {
    if (isLovablePreview()) {
      const filtered = companies
        .filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 10)
        .map(c => ({
          id: c.id,
          name: c.name,
          domain: c.website?.replace(/^https?:\/\//, "") || "",
          logoUrl: null,
        }));
      setCompanySuggestions(filtered);
      return;
    }
    if (!search.trim()) { setCompanySuggestions([]); return; }
    try {
      const result = await searchCompanies({
        variables: { workspaceId: String(workspaceId), search: search.trim(), first: 10 },
      });
      const accounts = result.data?.accounts?.edges
        ?.map(edge => edge?.node)
        .filter(Boolean)
        .map(account => ({
          id: account.id,
          name: account.properties?.name || account.domain || "Unknown",
          domain: account.domain || "",
          logoUrl: (account.properties as any)?.logoUrl || null,
        })) || [];
      setCompanySuggestions(accounts);
    } catch (error) {
      console.error("Error searching companies:", error);
      setCompanySuggestions([]);
    }
  }, [workspaceId, searchCompanies]);

  useEffect(() => {
    if (!showCompanyDropdown) return;
    const timer = setTimeout(() => fetchCompanySuggestions(companySearch), 200);
    return () => clearTimeout(timer);
  }, [companySearch, showCompanyDropdown, fetchCompanySuggestions]);

  useEffect(() => {
    const domain = extractDomain(email);
    if (domain && domain.includes(".")) {
      const known = domainCompanyMap[domain];
      if (known) {
        setCompanyDomain(domain);
        setCompanyName(known.name);
        setDomainMatched(true);
        return;
      }
      // Email domain matches an already-set company (e.g. from prefill) — keep it.
      if (companyDomain && domain === companyDomain) return;
    }
    if (domainMatched) {
      setCompanyDomain("");
      setCompanyName("");
      setDomainMatched(false);
    }
  }, [email]);

  // The dummy domainCompanyMap above only covers preview data; in production look up real Accounts by domain.
  useEffect(() => {
    if (isLovablePreview()) return;
    if (accountId) return;
    if (!workspaceId) return;
    const domain = extractDomain(email);
    if (!domain || !domain.includes(".")) return;
    if (domainMatched && companyDomain.toLowerCase() === domain.toLowerCase()) return;
    if (companyName && !domainMatched) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await searchCompanies({
          variables: { workspaceId: String(workspaceId), search: domain, first: 5 },
        });
        if (cancelled) return;
        if (extractDomain(email).toLowerCase() !== domain.toLowerCase()) return;
        const match = result.data?.accounts?.edges
          ?.map(edge => edge?.node)
          .find(node => node?.domain?.toLowerCase() === domain.toLowerCase());
        if (match) {
          setCompanyDomain(match.domain || domain);
          setCompanyName(match.properties?.name || match.domain || domain);
          setDomainMatched(true);
        }
      } catch (error) {
        console.error("Error looking up account by domain:", error);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [email, workspaceId, accountId, searchCompanies, domainMatched, companyDomain, companyName]);

  const handleEmailBlur = useCallback(async () => {
    const isValid = email.includes("@") && email.includes(".");
    if (!isValid || enrichedRef.current) return;
    enrichedRef.current = true;
    setEnriching(true);
    try {
      const data = await enrichPerson(email);
      if (data) {
        if (data.firstName && !firstName) setFirstName(data.firstName);
        if (data.lastName && !lastName) setLastName(data.lastName);
        if (data.title && !jobTitle) setJobTitle(data.title);
        if (data.phone && !phone) setPhone(formatPhone(data.phone));
        if (data.location && !location) setLocation(data.location);
        if (data.linkedin && !linkedin) setLinkedin(data.linkedin);
        if (data.company && !companyName) {
          setCompanyName(data.company);
          const domain = extractDomain(email);
          if (domain) {
            setCompanyDomain(domain);
            setDomainMatched(true);
          }
        }
      }
    } catch (e) {
      console.error("Enrichment failed:", e);
    } finally {
      setEnriching(false);
    }
  }, [email, firstName, lastName, jobTitle, phone, location, linkedin, companyName]);

  const reset = () => {
    setEmail(""); setFirstName(""); setLastName("");
    setJobTitle(""); setPhone(""); setLocation("");
    setLinkedin("");
    setCompanyName(""); setCompanyDomain(""); setCompanySearch("");
    setShowCompanyDropdown(false); setDomainMatched(false);
    setEditingName(false); setEditNameValue("");
    setEnriching(false); enrichedRef.current = false;
    setShowAddCompanyForm(false);
  };

  const resolveOrCreateAccount = useCallback(async (): Promise<string | undefined> => {
    const nameTrimmed = companyName.trim();
    const domainTrimmed = companyDomain.trim();
    if (!nameTrimmed && !domainTrimmed) return undefined;
    if (!workspaceId) return undefined;

    try {
      const searchTerm = domainTrimmed || nameTrimmed;
      const existing = await searchCompanies({
        variables: { workspaceId: String(workspaceId), search: searchTerm, first: 5 },
      });
      const match = existing.data?.accounts?.edges
        ?.map(edge => edge?.node)
        .find(node => {
          if (!node) return false;
          const nodeDomain = node.domain?.toLowerCase() || '';
          const nodeName = (node.properties?.name || '').toLowerCase();
          return (
            (domainTrimmed && nodeDomain === domainTrimmed.toLowerCase()) ||
            (nameTrimmed && nodeName === nameTrimmed.toLowerCase())
          );
        });
      if (match?.id) return match.id;

      const locationParts = location.split(',').map(s => s.trim());
      const created = await createAccountMutation({
        variables: {
          input: {
            domain: domainTrimmed || nameTrimmed,
            workspaceId: workspaceId as any,
            properties: {
              name: nameTrimmed || domainTrimmed,
              city: locationParts[0] || undefined,
              state: locationParts[1] || undefined,
              country: locationParts[2] || undefined,
            },
          },
        },
      });
      return created.data?.createAccount?.account?.id;
    } catch (error) {
      console.error('Failed to resolve/create account for person:', error);
      return undefined;
    }
  }, [companyName, companyDomain, workspaceId, searchCompanies, createAccountMutation, location]);

  const handleSubmit = async () => {
    if (!firstName.trim() || !email.trim()) return;

    if (useBackend) {
      try {
        const locationParts = location.split(',').map(s => s.trim());
        const city = locationParts[0] || '';
        const state = locationParts[1] || '';
        const country = locationParts[2] || '';

        const resolvedAccountId = accountId ?? (await resolveOrCreateAccount());

        const created = await createContact({
          primaryEmail: email.trim(),
          properties: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            jobTitle: jobTitle.trim(),
            phone: phone.trim(),
            city,
            state,
            country,
            linkedinProfileUrl: linkedin.trim() ? `linkedin.com/in/${linkedin.trim()}` : undefined,
            accountIds: resolvedAccountId ? [resolvedAccountId] : undefined,
          },
        });

        if (created?.externalId) {
          await onCreated?.({
            externalId: created.externalId,
            primaryEmail: created.primaryEmail ?? email.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
          });
        }

        reset();
        onOpenChange(false);
      } catch (error) {
        console.error('Failed to create contact:', error);
      }
    } else {
      onAdd?.({
        firstName: firstName.trim(), lastName: lastName.trim(),
        email: email.trim(), company: companyName, companyUrl: companyDomain,
        title: jobTitle.trim(), phone: phone.trim(), location: location.trim(),
        linkedin: linkedin.trim() ? `linkedin.com/in/${linkedin.trim()}` : "",
      });
      // Synthetic ext_id so preview-mode wiring can chain follow-ups.
      // Prefix matches the production ``c|ddb|...`` shape closely enough
      // to be recognisable; preview never round-trips this value to a
      // real backend.
      await onCreated?.({
        externalId: `c|preview|${email.trim().toLowerCase()}`,
        primaryEmail: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      reset();
      onOpenChange(false);
    }
  };

  const handleCancel = () => { reset(); onOpenChange(false); };
  const startEditName = () => { setEditNameValue(companyName); setEditingName(true); };
  const confirmEditName = () => { setCompanyName(editNameValue.trim() || companyName); setEditingName(false); };
  const isValidEmail = email.includes("@") && email.includes(".");

  const handlePhoneChange = (value: string) => {
    setPhone(formatPhone(value));
  };

  const searchTextForNewCompany = companySearch.trim();

  const handleCompanyCreated = (newCompany: { name: string; website: string }) => {
    setCompanyName(newCompany.name);
    if (newCompany.website) {
      const d = newCompany.website.replace(/^(https?:\/\/)?(www\.)?/, "").split("/")[0];
      setCompanyDomain(d);
      setDomainMatched(true);
    }
    setCompanySearch("");
    setShowAddCompanyForm(false);
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) handleCancel(); else onOpenChange(v); }}>
      <SheetContent expandable className="sm:max-w-[50vw] w-full overflow-y-auto p-0 flex flex-col">
        {showAddCompanyForm ? (
          <AddCompanyForm
            onCompanyCreated={handleCompanyCreated}
            onCancel={() => setShowAddCompanyForm(false)}
            initialName={searchTextForNewCompany}
            showBackButton
          />
        ) : (
          <>
            <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
              <SheetTitle className="text-lg font-display">{t("common.addEntity", { entity: singular.people })}</SheetTitle>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="person-email" className="text-sm">{t("fields.people.email")} <span className="text-destructive">*</span></Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="person-email" value={email} onChange={e => { setEmail(e.target.value); enrichedRef.current = false; }} onBlur={handleEmailBlur} placeholder={t("forms.person.emailPlaceholder")} className="pl-9" autoFocus />
                  {enriching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
                </div>
                {enriching && (
                  <p className="text-xs text-muted-foreground animate-pulse">{t("forms.person.lookingUp")}</p>
                )}
                {email && !isValidEmail && (
                  <p className="text-xs text-destructive">{t("forms.person.invalidEmail")}</p>
                )}
              </div>

              {/* Name fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="person-first" className="text-sm">{t("fields.people.firstName")} <span className="text-destructive">*</span></Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input id="person-first" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder={t("forms.person.firstPlaceholder")} className="pl-9" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="person-last" className="text-sm">{t("fields.people.lastName")} <span className="text-destructive">*</span></Label>
                  <Input id="person-last" value={lastName} onChange={e => setLastName(e.target.value)} placeholder={t("forms.person.lastPlaceholder")} />
                </div>
              </div>

              {/* Title */}
              <div className="space-y-1.5">
                <Label htmlFor="person-title" className="text-sm">{t("fields.people.title")}</Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="person-title" value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder={t("forms.person.titlePlaceholder")} className="pl-9" />
                </div>
              </div>

              {/* Company */}
              <div className="space-y-1.5">
                <Label className="text-sm">{t("fields.people.company")}</Label>
                {domainMatched && companyName ? (
                  <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
                    <img
                      src={faviconUrl(companyDomain)}
                      alt={companyName}
                      className="h-5 w-5 rounded shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    {editingName ? (
                      <div className="flex items-center gap-1.5 flex-1">
                        <Input
                          value={editNameValue}
                          onChange={e => setEditNameValue(e.target.value)}
                          className="h-7 text-sm py-0"
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') confirmEditName(); if (e.key === 'Escape') setEditingName(false); }}
                        />
                        <button onClick={confirmEditName} className="text-muted-foreground hover:text-foreground">
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => setEditingName(false)} className="text-muted-foreground hover:text-foreground">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="text-sm font-medium">{companyName}</span>
                        <button
                          onClick={() => { setDomainMatched(false); setCompanyName(""); setCompanyDomain(""); }}
                          className="text-xs text-muted-foreground hover:text-foreground ml-auto"
                        >
                          {t("forms.actions.change")}
                        </button>
                        <button onClick={startEditName} className="text-muted-foreground hover:text-foreground" title={t("forms.person.editCompanyName")}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={companyName || companySearch}
                      onChange={e => { setCompanySearch(e.target.value); setCompanyName(""); }}
                      onFocus={() => setShowCompanyDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 150)}
                      placeholder={t("forms.company.searchPlaceholder")}
                      className="pl-9"
                    />
                    {showCompanyDropdown && (
                      <div className="absolute z-10 top-full mt-1 w-full border border-border rounded-lg bg-popover shadow-lg max-h-48 overflow-y-auto">
                        {companiesLoading && (
                          <div className="flex items-center gap-2 px-3 py-2 text-muted-foreground text-xs">
                            <Loader2 className="h-3 w-3 animate-spin" /> {t("forms.actions.searching")}
                          </div>
                        )}
                        {!companiesLoading && companySuggestions.length === 0 && companySearch && (
                          <div className="px-3 py-2 text-muted-foreground text-xs">{t("forms.person.noCompanies")}</div>
                        )}
                        {companySuggestions.map(c => (
                          <button
                            key={c.id}
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => {
                              setCompanyName(c.name);
                              setCompanyDomain(c.domain);
                              setCompanySearch("");
                              setShowCompanyDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition flex items-center gap-2"
                          >
                            <CompanyLogo name={c.name} domain={c.domain} />
                            {c.name}
                          </button>
                        ))}
                        {/* Add new company option */}
                        <button
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => {
                            setShowCompanyDropdown(false);
                            setShowAddCompanyForm(true);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition flex items-center gap-2 border-t border-border text-accent"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          <span>{searchTextForNewCompany ? t("forms.company.createNewNamed", { name: searchTextForNewCompany }) : t("forms.company.createNew")}</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* LinkedIn */}
              <div className="space-y-1.5">
                <Label htmlFor="person-linkedin" className="text-sm">LinkedIn</Label>
                <div className="relative flex items-center">
                  <LinkedInIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" />
                  <span className="absolute left-9 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">/</span>
                  <Input
                    id="person-linkedin"
                    value={linkedin}
                    onChange={e => setLinkedin(e.target.value)}
                    onBlur={() => setLinkedin(normalizeLinkedin(linkedin))}
                    placeholder={t("forms.person.linkedinPlaceholder")}
                    className="pl-[3.25rem]"
                  />
                </div>
              </div>

              {/* Phone + Location */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="person-phone" className="text-sm">{t("fields.people.phone")}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input id="person-phone" value={phone} onChange={e => handlePhoneChange(e.target.value)} placeholder={t("forms.person.phonePlaceholder")} className="pl-9" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">{t("fields.people.location")}</Label>
                  <EditableLocationField
                    value={location}
                    onChange={setLocation}
                    className="h-9 rounded-md border border-input bg-background px-3 hover:border-accent/50 transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-border px-6 py-4 flex justify-end gap-2">
              <Button variant="outline" onClick={handleCancel} disabled={creating}>{t("common.cancel")}</Button>
              <Button onClick={handleSubmit} disabled={!firstName.trim() || !isValidEmail || creating}>
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t("forms.actions.adding")}
                  </>
                ) : (
                  <>{t("common.addEntity", { entity: singular.people })}</>
                )}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
